import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Recipe } from '@/types/api';
import { AdvancedFilters } from '@/components/recipes/AdvancedFilters';
import { RecipeGrid } from '@/components/recipes/RecipeGrid';
import { Pagination } from '@/components/ui/pagination';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { useAdvancedRecipeSearch } from '@/hooks';
// Note: useHybridFavorites is used internally by RecipeCard components
import { 
  Search
} from 'lucide-react';

export function RecipeDiscovery() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Note: Individual RecipeCards now handle favorites internally
  
  // Recipe search hook
  const {
    filters,
    results,
    isLoading,
    isError,
    error,
    totalCount,
    currentPage,
    totalPages,
    isEmpty,
    isRandomMode,
    updateFilters,
    clearFilters,
    setPage
  } = useAdvancedRecipeSearch({
    defaultLimit: 12,
    enableRandomFallback: true
  });


  // Handle AI-powered text search
  const handleSearch = useCallback((query: string) => {
    if (!query.trim()) return;
    updateFilters({ 
      query: query.trim(), 
      ingredient: undefined, // Limpiar ingrediente cuando se busca por texto
      category: undefined,   // Limpiar filtros avanzados
      area: undefined,
      page: 1 
    });
    setShowAdvanced(false); // Ocultar filtros avanzados al buscar
  }, [updateFilters]);

  // Handle recipe actions
  const handleViewRecipe = useCallback((recipe: Recipe) => {
    navigate(`/recipes/${recipe.id}`);
  }, [navigate]);

  const handleAddToMealPlan = useCallback((recipe: Recipe) => {
    // TODO: Implement add to meal plan
    console.log('Add to meal plan:', recipe);
  }, []);

  // Note: RecipeCard now handles favorites internally with useHybridFavorites


  return (
    <>
      {/* Header */}
      <PageHeader
        title="Descubrir Recetas"
        icon={<Search className="h-5 w-5" />}
      />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Controls Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col gap-6">
            {/* AI-Powered Search */}
            <div className="flex justify-center">
              <div className="w-full max-w-2xl">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Busca cualquier receta... ej: 'algo rápido con pollo', 'postres sin azúcar'"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch(searchQuery);
                      }
                    }}
                    disabled={isLoading}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-primary focus:border-transparent
                               placeholder:text-gray-500 text-base"
                  />
                  <button
                    onClick={() => handleSearch(searchQuery)}
                    disabled={isLoading || !searchQuery.trim()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 
                               p-2 text-gray-400 hover:text-primary disabled:opacity-50
                               transition-colors duration-200"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
                
                {/* AI Badge */}
                <div className="flex items-center justify-center mt-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-purple-50 to-blue-50 
                                  border border-purple-200 rounded-full text-xs text-purple-700">
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                    Potenciado por IA - Busca en lenguaje natural
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Filters Toggle */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs"
              >
                {showAdvanced ? 'Ocultar' : 'Mostrar'} filtros avanzados
              </Button>
            </div>

            {/* Advanced Filters */}
            {showAdvanced && (
              <AdvancedFilters
                category={filters.category}
                area={filters.area}
                onApply={(f) => updateFilters({ ...f, query: searchQuery, page: 1 })}
                onClear={() => {
                  updateFilters({ category: undefined, area: undefined, query: '', page: 1 });
                  setSearchQuery('');
                }}
                disabled={isLoading}
              />
            )}

            {/* Results Count */}
            {!isLoading && totalCount > 0 && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
                <Search className="h-4 w-4" />
                <span className="font-medium">
                  {totalCount} receta{totalCount !== 1 ? 's' : ''} encontrada{totalCount !== 1 ? 's' : ''}
                </span>
                {filters.query && (
                  <span className="text-gray-500">para "{filters.query}"</span>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Error State */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-800 mb-2">
              <Search className="h-4 w-4" />
              <span className="font-medium">Error al buscar recetas</span>
            </div>
            <p className="text-red-700 text-sm">
              {error?.message || 'Ocurrió un error inesperado'}
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.reload()}
              className="mt-3"
            >
              Intentar de nuevo
            </Button>
          </div>
        )}

        {/* Recipe Grid */}
        <RecipeGrid
          recipes={results}
          onAddToMealPlan={handleAddToMealPlan}
          onViewRecipe={handleViewRecipe}
          loading={isLoading}
          className="mb-8"
        />

        {/* Empty State - Custom message for no filters */}
        {isEmpty && !isLoading && !isError && !isRandomMode && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron recetas</h3>
            <p className="text-gray-500 text-sm max-w-sm mx-auto mb-4">
              Prueba con diferentes términos de búsqueda o ajusta los filtros.
            </p>
            <Button 
              variant="outline"
              onClick={clearFilters}
            >
              Limpiar filtros
            </Button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && !isRandomMode && (
          <div className="flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
              disabled={isLoading}
            />
          </div>
        )}
      </div>
    </>
  );
}
